import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldAlert, Copy, KeyRound, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAdminMfa } from "@/hooks/useAdminMfa";
import { useAuth } from "@/hooks/useAuth";
import AdminStepUpInterceptor from "@/components/admin/AdminStepUpInterceptor";

/**
 * Wraps the whole admin area. Nobody reaches an admin screen without an
 * authenticator set up and a code confirmed for this session — the same rules
 * the backend enforces on every privileged call.
 */
export default function AdminMfaGate({ children }: { children: React.ReactNode }) {
  const { status, loading, error, refresh } = useAdminMfa();

  if (loading && !status) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!status) {
    return (
      <Shell title="Security check unavailable" icon={ShieldAlert}>
        <Alert variant="destructive">
          <AlertDescription>{error || "We could not confirm your security status."}</AlertDescription>
        </Alert>
        <Button className="w-full" onClick={() => refresh()}>Try again</Button>
      </Shell>
    );
  }

  if (!status.enrolled) return <EnrolmentScreen />;
  if (!status.session_verified) return <VerificationScreen />;

  return (
    <>
      {children}
      <AdminStepUpInterceptor />
    </>
  );
}

function Shell({
  title,
  description,
  icon: Icon,
  children,
}: {
  title: string;
  description?: string;
  icon: any;
  children: React.ReactNode;
}) {
  const { signOut } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 p-3 rounded-full bg-primary/10 w-fit">
            <Icon className="h-7 w-7 text-primary" />
          </div>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-4">
          {children}
          <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => signOut()}>
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function EnrolmentScreen() {
  const { startEnrolment, confirmEnrolment } = useAdminMfa();
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);

  const begin = async () => {
    setBusy(true);
    try {
      setSetup(await startEnrolment());
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      setCodes(await confirmEnrolment(code.trim()));
      toast.success("Two-factor is now active");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (codes) {
    return (
      <Shell
        title="Save your backup codes"
        description="Each code works once if you lose your authenticator. Store them somewhere safe."
        icon={KeyRound}
      >
        <div className="grid grid-cols-2 gap-2">
          {codes.map((c) => (
            <code key={c} className="text-sm font-mono bg-muted rounded px-2 py-1.5 text-center">{c}</code>
          ))}
        </div>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => {
            navigator.clipboard.writeText(codes.join("\n"));
            toast.success("Backup codes copied");
          }}
        >
          <Copy className="h-4 w-4 mr-2" /> Copy all codes
        </Button>
        <Button className="w-full" onClick={() => window.location.reload()}>
          I've saved them — continue
        </Button>
      </Shell>
    );
  }

  return (
    <Shell
      title="Set up two-factor access"
      description="Staff accounts require an authenticator app before entering the admin area."
      icon={ShieldCheck}
    >
      {!setup ? (
        <Button className="w-full" onClick={begin} disabled={busy}>
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Start setup
        </Button>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-center bg-white p-4 rounded-lg">
            <QRCodeSVG value={setup.uri} size={168} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Or enter this key manually</Label>
            <div className="flex gap-2">
              <code className="flex-1 text-xs font-mono bg-muted rounded px-2 py-2 break-all">{setup.secret}</code>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  navigator.clipboard.writeText(setup.secret);
                  toast.success("Key copied");
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="enrol-code">6-digit code from your app</Label>
            <Input
              id="enrol-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              className="text-center text-lg tracking-[0.4em] font-mono"
            />
          </div>
          <Button className="w-full" onClick={confirm} disabled={busy || code.length !== 6}>
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Activate two-factor
          </Button>
        </div>
      )}
    </Shell>
  );
}

function VerificationScreen() {
  const { verifySession, verifyRecoveryCode } = useAdminMfa();
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (recovery) await verifyRecoveryCode(code.trim());
      else await verifySession(code.trim());
      toast.success("Verified");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      title="Confirm it's you"
      description="Enter the current code from your authenticator app to open the admin area."
      icon={ShieldCheck}
    >
      <div className="space-y-1">
        <Label htmlFor="verify-code">{recovery ? "Backup code" : "6-digit code"}</Label>
        <Input
          id="verify-code"
          autoFocus
          inputMode={recovery ? "text" : "numeric"}
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(recovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder={recovery ? "XXXXX-XXXXX" : "000000"}
          className="text-center text-lg tracking-[0.3em] font-mono"
        />
      </div>
      <Button className="w-full" onClick={submit} disabled={busy || code.length < 6}>
        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Continue
      </Button>
      <Button
        variant="link"
        size="sm"
        className="w-full"
        onClick={() => { setRecovery(!recovery); setCode(""); }}
      >
        {recovery ? "Use my authenticator app instead" : "Use a backup code"}
      </Button>
    </Shell>
  );
}
